import { Skeleton, VStack } from '@chakra-ui/react';
import React from 'react';
import { useQueryClient } from 'react-query';
import { Navigate } from 'react-router-dom';

import { AppError } from '../../../../../framework/models/errors';
import { ListUtils } from '../../../../../framework/utils/ListUtils';
import { TypeUtils } from '../../../../../framework/utils/UtilityTypes';
import StyledButton, { AddButton } from '../../../../components/StyledButton';
import StyledList from '../../../../components/StyledList';
import { pageKeywords, pageNames, pagePaths } from '../../../../constants/Page';
import { useGlobalStateMachine } from '../../../../GlobalStore';
import { useEngeneNavigate } from '../../../../hooks/navigate';
import {
  OUTSOURCE_MEMBER_CONTEXT_NAMES,
  usePostOutsourceMemberSearch,
} from '../../../../hooks/queries/outsourceMember';
import useToastHook from '../../../../hooks/ToastHook';
import { MasterKey } from '../../../../models/gen/masterKey';
import { OutsourceMemberSearchResult } from '../../../../models/gen/outsourceMemberSearchResult';
import { useMasterContext } from '../../../MasterProvider';
import { useSizeContext } from '../../../SizeProvider';
import Wireframe from '../../../wireframe/Wireframe';

/**
 * 外注メンバ管理画面
 * @returns
 */
const OutsourceMember: React.FunctionComponent = () => {
  const { state } = useGlobalStateMachine();
  if (TypeUtils.isNil(state.userInfo)) {
    return <Navigate to={pagePaths.ROOT} />;
  }

  const queryClient = useQueryClient();
  const members = queryClient.getQueryData<OutsourceMemberSearchResult>(
    OUTSOURCE_MEMBER_CONTEXT_NAMES.POST_OUTSOURCE_MEMBER_SEARCH
  );
  const setMembers = React.useCallback(
    (result: OutsourceMemberSearchResult) =>
      queryClient.setQueryData(
        OUTSOURCE_MEMBER_CONTEXT_NAMES.POST_OUTSOURCE_MEMBER_SEARCH,
        result
      ),
    [queryClient]
  );
  const { toast } = useToastHook();
  const postOutsourceMemberSearchResult = usePostOutsourceMemberSearch();
  React.useState(() =>
    postOutsourceMemberSearchResult.mutate(
      {
        outsourcerCode: state.userInfo!.outsourcer_code,
      },
      {
        onSuccess: (result) => {
          if (!TypeUtils.isNil(result)) {
            setMembers(result);
          }
        },
        onError: (error) =>
          toast(
            error instanceof AppError ? error : '外注メンバの取得に失敗しました'
          ),
      }
    )
  );

  const sizes = useSizeContext();

  return (
    <Wireframe title={pageNames.WORKER_MANAGEMENT}>
      <Skeleton
        as={VStack}
        isLoaded={!postOutsourceMemberSearchResult.isLoading}
        width={sizes.width.styledMessageList}
      >
        <Content data={members} />
      </Skeleton>
    </Wireframe>
  );
};

const Content: React.FunctionComponent<{
  data?: OutsourceMemberSearchResult;
}> = ({ data }) => {
  const navigate = useEngeneNavigate();
  const master = useMasterContext();

  // if (TypeUtils.isNil(data)) {
  //   return <NavigateGoBack />;
  // }

  return (
    <>
      <StyledList
        items={
          data?.results
            ?.filter(
              (u) => master.getBy(MasterKey.Roles, u.roleId!)?.roleType === 2
            )
            .sort(ListUtils.sort((u) => u.userId))
            .map(({ name, userId }, index) => (
              <StyledButton
                key={index}
                onClick={() =>
                  navigate(
                    `${pagePaths.WORKER}/${userId}/${pageKeywords.mode.VIEW}`
                  )
                }
                variant="link"
              >
                {name}
              </StyledButton>
            )) || []
        }
      />
      <AddButton onClick={() => navigate(pagePaths.WORKER)} />
    </>
  );
};

export default OutsourceMember;
